// API utility functions for making authenticated requests
import {
  downloadFileWithAuth,
  fetchWithAuth,
  getApi,
  getAuthHeaders,
} from './apiTransport';
import {
  getFOSDecisionById,
  getFOSFacets,
  getFOSProjections,
  getFOSSignalProjections,
  getFOSStats,
  getSimilarFOSDecisions,
  refreshFOSSignalProjections,
  searchFOSDecisions,
  searchFOSDecisionsGet,
} from '../services/fosExplorerApi';

export {
  downloadFileWithAuth,
  fetchWithAuth,
  getApi,
  getAuthHeaders,
} from './apiTransport';

export {
  getFOSDecisionById,
  getFOSFacets,
  getFOSProjections,
  getFOSSignalProjections,
  getFOSStats,
  getSimilarFOSDecisions,
  refreshFOSSignalProjections,
  searchFOSDecisions,
  searchFOSDecisionsGet,
} from '../services/fosExplorerApi';

export type {
  FOSClusterInfo,
  FOSCorpusStats,
  FOSDecisionCard,
  FOSDecisionDetail,
  FOSFacets,
  FOSProjectionData,
  FOSProjectionPoint,
  FOSSearchParams,
  FOSSearchResult,
  FOSSignalProjectionPoint,
  FOSSignalProjectionRefreshResponse,
  FOSSignalProjectionsResponse,
} from '../services/fosExplorerApi';

// Common API functions

/**
 * Get current user profile
 */
export const getCurrentUser = () => {
  return fetchWithAuth('/v1/auth/me');
};

/**
 * Get complaint data
 */
export const getComplaints = (timeframe = '30d', limit = 100) => {
  return fetchWithAuth(`/v1/complaints?timeframe=${timeframe}&limit=${limit}`);
};

/**
 * Get paginated complaints with filters
 */
export const getPaginatedComplaints = (page = 1, limit = 20, filters = {}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...filters
  });
  return fetchWithAuth(`/v1/complaints?${queryParams}`);
};

/**
 * Get a single complaint by ID
 */
export const getComplaintById = (caseId: string) => {
  return fetchWithAuth(`/v1/complaints/${caseId}`);
};

/**
 * Create a polling function that periodically calls an API endpoint
 * @param endpoint - The API endpoint to poll
 * @param interval - Poll interval in milliseconds (default: 10000ms)
 * @param params - Query parameters to include
 * @returns Object with start and stop methods to control polling
 */
export const createPollingFunction = (
  endpoint: string,
  interval: number = 10000,
  params: Record<string, string> = {}
) => {
  let timerId: number | null = null;
  
  const start = (callback: (data: any) => void, errorHandler?: (err: Error) => void) => {
    if (timerId) {
      // Already polling
      return;
    }
    
    const fetchData = async () => {
      try {
        const queryParams = new URLSearchParams(params);
        const url = Object.keys(params).length > 0 
          ? `${endpoint}?${queryParams}` 
          : endpoint;
        
        const data = await fetchWithAuth(url);
        callback(data);
      } catch (err) {
        if (errorHandler) {
          errorHandler(err as Error);
        } else {
          console.error('Polling error:', err);
        }
      }
    };
    
    // Fetch immediately on start
    fetchData();
    
    // Then set up interval
    timerId = window.setInterval(fetchData, interval);
    
    return () => stop(); // Return cleanup function
  };
  
  const stop = () => {
    if (timerId !== null) {
      window.clearInterval(timerId);
      timerId = null;
    }
  };
  
  return { start, stop };
};

/**
 * Submit a new complaint
 */
export const submitComplaint = (complaintData: any) => {
  return fetchWithAuth('/v1/classify', {
    method: 'POST',
    body: JSON.stringify(complaintData)
  });
};

/**
 * Reclassify an existing complaint, optionally with a custom prompt
 */
export const reclassifyComplaint = (caseId: string, customPrompt?: any) => {
  return fetchWithAuth(`/v1/reclassify/${caseId}`, {
    method: 'POST',
    body: customPrompt ? JSON.stringify({ custom_prompt: customPrompt }) : undefined
  });
};

/**
 * Delete a single complaint by ID
 */
export const deleteComplaint = (caseId: string) => {
  return fetchWithAuth(`/v1/complaints/${caseId}`, {
    method: 'DELETE'
  });
};

/**
 * Delete multiple complaints
 */
export const deleteMultipleComplaints = (caseIds: string[]) => {
  return fetchWithAuth('/v1/complaints', {
    method: 'DELETE',
    body: JSON.stringify({ case_ids: caseIds })
  });
};

/**
 * Get cluster data
 */
export const getClusters = () => {
  return fetchWithAuth('/v1/clusters');
};

/**
 * Get paginated clusters with filters
 */
export const getPaginatedClusters = (page = 1, limit = 20, filters = {}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...filters
  });
  return fetchWithAuth(`/v1/clusters?${queryParams}`);
};

/**
 * Get a single cluster by ID
 */
export const getClusterById = (clusterId: string) => {
  return fetchWithAuth(`/v1/clusters/${clusterId}`);
};

/**
 * Get explore clusters data with pagination and filters
 */
export const getExploreClusters = (page = 1, limit = 20, filters = {}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...filters
  });
  return fetchWithAuth(`/v1/explore/clusters?${queryParams}`);
};

/**
 * Get explore clusters summary data
 */
export const getExploreClustersSummary = () => {
  return fetchWithAuth('/v1/explore/clusters/summary');
};

/**
 * Get explore clusters timeline data
 */
export const getExploreClustersTimeline = (days = 30) => {
  return fetchWithAuth(`/v1/explore/clusters/timeline?days=${days}`);
};

/**
 * Options for cluster generation
 */
interface ClusteringOptions {
  timeframe?: string;
  limit?: number;
  minClusterSize?: number;
  similarityThreshold?: number;
  includeVulnerabilityAnalysis?: boolean;
}

/**
 * Generate new clusters using the real API (synchronous, may time out)
 */
export const generateClusters = (options: ClusteringOptions = {}) => {
  // Use the clustering endpoint from the real API
  const endpoint = `/v1/cluster`;
  
  return fetchWithAuth(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      timeframe: options.timeframe || '30d',
      limit: options.limit || 500,
      filters: {}, // Optional filters for the complaints
      clusteringOptions: {
        minClusterSize: options.minClusterSize || 5,
        similarityThreshold: options.similarityThreshold || 0.85,
        includeVulnerabilityAnalysis: options.includeVulnerabilityAnalysis !== false
      }
    })
  });
};

/**
 * Generate new clusters using a background job
 * This is the preferred method for real clustering as it handles long-running jobs
 */
export const generateClustersBackground = (options: ClusteringOptions = {}) => {
  // Use the background clustering endpoint 
  const endpoint = `/v1/cluster/background`;
  
  return fetchWithAuth(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      timeframe: options.timeframe || '30d',
      limit: options.limit || 500,
      filters: {}, // Optional filters for the complaints
      clusteringOptions: {
        minClusterSize: options.minClusterSize || 5,
        similarityThreshold: options.similarityThreshold || 0.85,
        includeVulnerabilityAnalysis: options.includeVulnerabilityAnalysis !== false
      }
    })
  });
};

/**
 * Check status of a background clustering job
 */
export const getClusterJobStatus = (jobId: string): Promise<ClusterJob> => {
  return fetchWithAuth(`/v1/cluster/job/${jobId}`);
};

// Define interface for job data (should match the one in ExploreClustersDashboard.tsx)
export interface ClusterJob {
  job_id: string;
  status: string;
  progress?: number;
  result?: any;
  created_at: string;
  completed_at?: string;
  error?: string;
}

/**
 * Get list of all clustering jobs
 */
export const getAllClusterJobs = (): Promise<ClusterJob[]> => {
  return fetchWithAuth('/v1/cluster/jobs');
};

/**
 * Get diagnostic information about the clustering system
 */
export const getClusteringDiagnostics = () => {
  return fetchWithAuth('/v1/diagnostics');
};

/**
 * DEBUG: Get all clusters in the database
 */
export const getAllClustersDebug = () => {
  return fetchWithAuth('/v1/debug/all-clusters');
};

// Signals API Functions

/**
 * Get paginated signals with filters
 */
export const getPaginatedSignals = (page = 1, limit = 20, filters: Record<string, any> = {}) => {
  const offset = (page - 1) * limit;

  // Build params manually so that array-valued filters serialize as repeated keys
  const params = new URLSearchParams();
  params.append('offset', offset.toString());
  params.append('limit', limit.toString());

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach(v => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  });

  const qs = params.toString();
  return fetchWithAuth(`/v1/signals/?${qs}`);
};

/**
 * Get a single signal by ID
 */
export const getSignalById = (signalId: string) => {
  return fetchWithAuth(`/v1/signals/${signalId}`);
};

/**
 * Create/ingest a new signal
 */
export const ingestSignal = (signalData: any) => {
  return fetchWithAuth('/v1/signals/ingest', {
    method: 'POST',
    body: JSON.stringify(signalData)
  });
};

/**
 * Update a signal
 */
export const updateSignal = (signalId: string, signalData: any) => {
  return fetchWithAuth(`/v1/signals/${signalId}`, {
    method: 'PUT',
    body: JSON.stringify(signalData)
  });
};

/**
 * Delete a signal
 */
export const deleteSignal = (signalId: string) => {
  return fetchWithAuth(`/v1/signals/${signalId}`, {
    method: 'DELETE'
  });
};

/**
 * Bulk delete signals
 */
export const deleteSignalsBulk = (ids: string[]) => {
  return fetchWithAuth('/v1/signals/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ signal_ids: ids })
  });
};

/**
 * Export signals to CSV or JSON format
 * @param filters - Filter parameters matching the current view/filters
 * @param format - Export format ('csv' or 'json')
 * @returns Promise that resolves when download is complete
 */
export const exportSignals = async (filters: Record<string, any>, format: 'csv' | 'json'): Promise<void> => {
  // Build query parameters
  const params = new URLSearchParams();
  params.append('format', format);

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach(v => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  });

  const qs = params.toString();
  const url = `/v1/signals/export?${qs}`;
  const filename = `signals-export-${new Date().toISOString().split('T')[0]}.${format}`;

  await downloadFileWithAuth(url, filename);
};

/** Debug: Expand taxonomy category keys to label groups and resolved metadata */
export const debugExpandTaxonomyKeys = (taxonomyKeys: string[], rootCauseKeys: string[] = []) => {
  const params = new URLSearchParams();
  taxonomyKeys.forEach(k => params.append('taxonomy_category_keys', k));
  rootCauseKeys.forEach(k => params.append('root_cause_category_keys', k));
  const qs = params.toString();
  return fetchWithAuth(`/v1/signals/debug/taxonomy-expand${qs ? `?${qs}` : ''}`);
};

/**
 * Create a new signal (simpler endpoint)
 */
export const createSignal = (signalData: any) => {
  return fetchWithAuth('/v1/signals', {
    method: 'POST',
    body: JSON.stringify(signalData)
  });
};

/**
 * Reclassify a signal using AI
 */
export const reclassifySignal = (signalId: string) => {
  return fetchWithAuth(`/v1/signals/${signalId}/reclassify`, {
    method: 'POST'
  });
};

/**
 * Bulk reclassify multiple signals using AI
 */
export const bulkReclassifySignals = (signalIds: string[]) => {
  return fetchWithAuth(`/v1/signals/bulk-reclassify`, {
    method: 'POST',
    body: JSON.stringify({ signal_ids: signalIds })
  });
};

/**
 * Utility to check if a user is an admin (case-insensitive)
 */
export function isAdminUser(user: { role?: string } | null | undefined): boolean {
  return !!user && typeof user.role === 'string' && user.role.toLowerCase() === 'admin';
}

export interface OrganizationDisplaySettings {
  show_costs: boolean;
  show_langchain_details: boolean;
  root_cause_classification_enabled: boolean;
  root_cause_classification_changed_at: string | null;
}

export const DEFAULT_ORGANIZATION_DISPLAY_SETTINGS: OrganizationDisplaySettings = {
  show_costs: true,
  show_langchain_details: true,
  root_cause_classification_enabled: true,
  root_cause_classification_changed_at: null,
};

/**
 * Get organization display settings
 * @returns Promise with display settings
 */
export const getOrganizationDisplaySettings = async (): Promise<OrganizationDisplaySettings> => {
  try {
    const response = await fetchWithAuth('/v1/organization-settings/display-config', {
      method: 'GET'
    });
    return {
      ...DEFAULT_ORGANIZATION_DISPLAY_SETTINGS,
      ...response,
    };
  } catch (error) {
    console.error('Error fetching organization display settings:', error);
    // Default settings if we can't fetch them
    return DEFAULT_ORGANIZATION_DISPLAY_SETTINGS;
  }
};

/**
 * Update organization display settings (admin only)
 * @param settings - Object with organization display settings
 * @returns Promise with updated settings
 */
export const updateOrganizationDisplaySettings = async (
  settings: Partial<OrganizationDisplaySettings>
): Promise<any> => {
  const response = await fetchWithAuth('/v1/organization-settings', {
    method: 'PATCH',
    body: JSON.stringify(settings)
  });
  return response;
}

/**
 * Process uploaded file to extract signal data using LLM
 */
export const processFileForSignal = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return fetchWithAuth('/v1/signals/process-file', {
    method: 'POST',
    body: formData
  });
};

/**
 * Process uploaded file, create draft signal, and save evidence in one operation
 * Returns draft signal with extracted data for user review/editing
 */
export const processFileAndCreateDraft = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return fetchWithAuth('/v1/signals/process-file-and-create-draft', {
    method: 'POST',
    body: formData
  });
};

/**
 * Process multiple files, merge extractions, and create a single draft signal.
 * Returns merged signal data with evidence IDs and source extraction results.
 */
export const createDraftFromFiles = async (
  files: File[],
  datasetId?: string
): Promise<import('../types/multi-document').MergedDraftSignalResponse> => {
  const formData = new FormData();
  
  // Append all files with the same field name 'files'
  files.forEach((file) => {
    formData.append('files', file);
  });
  
  // Add optional dataset_id
  if (datasetId) {
    formData.append('dataset_id', datasetId);
  }
  
  return fetchWithAuth('/v1/signals/create-draft-from-files', {
    method: 'POST',
    body: formData
  }) as Promise<import('../types/multi-document').MergedDraftSignalResponse>;
};

/**
 * Finalize a draft signal by moving it to OPEN status and triggering processing
 */
export const finalizeDraftSignal = (signalId: string) => {
  return fetchWithAuth(`/v1/signals/${signalId}/finalize`, {
    method: 'POST'
  });
};

/**
 * @deprecated Use the new async import system with uploadSignalFile and startSignalImport from signalImportService
 * This old synchronous endpoint is no longer recommended as it doesn't support progress tracking
 */
export const processFileForMultipleSignals = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return fetchWithAuth('/v1/signals/process-file-multi', {
    method: 'POST',
    body: formData
  });
};

// Ombudsman Decisions API Functions

/**
 * Get paginated ombudsman decisions with filters
 */
export const getOmbudsmanDecisions = (
  skip = 0, 
  limit = 100, 
  filters: Record<string, string> = {}
) => {
  const queryParams = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
    ...filters
  });
  return fetchWithAuth(`/v1/ombudsman-decisions/?${queryParams}`);
};

/**
 * Get a single ombudsman decision by ID
 */
export const getOmbudsmanDecisionById = (decisionId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/${decisionId}`);
};

/**
 * Import ombudsman decisions from JSON file
 */
export const importOmbudsmanDecisions = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return fetchWithAuth('/v1/ombudsman-decisions/import', {
    method: 'POST',
    body: formData
  });
};

/**
 * Delete ombudsman decisions (all or specific IDs)
 */
export const deleteOmbudsmanDecisions = (decisionIds?: string[]) => {
  return fetchWithAuth('/v1/ombudsman-decisions/', {
    method: 'DELETE',
    body: decisionIds ? JSON.stringify(decisionIds) : undefined
  });
};

/**
 * Get ombudsman decisions statistics
 */
export const getOmbudsmanStatistics = () => {
  return fetchWithAuth('/v1/ombudsman-decisions/stats/summary');
};

/**
 * Get ombudsman advice for a signal
 */
export const getSignalOmbudsmanAdvice = (signalId: string) => {
  return fetchWithAuth(`/v1/signals/${signalId}/ombudsman-advice`);
};

/**
 * Get signals related to a specific ombudsman decision
 */
export const getDecisionRelatedSignals = (decisionId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/${decisionId}/related-signals`);
};

/**
 * List FOS simulations
 */
export const listSimulations = (signalId?: string, limit: number = 50, offset: number = 0) => {
  const params = new URLSearchParams();
  if (signalId) params.append('signal_id', signalId);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  return fetchWithAuth(`/v1/ombudsman-decisions/simulations?${params.toString()}`);
};

/**
 * Get a simulation by ID
 */
export const getSimulation = (simulationId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/simulations/${simulationId}`);
};

/**
 * Delete a simulation
 */
export const deleteSimulation = (simulationId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/simulations/${simulationId}`, {
    method: 'DELETE'
  });
};

/**
 * Get ombudsman decision by DRN reference
 */
export const getDecisionByReference = (reference: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/decisions/${reference}`);
};

/**
 * Create a new FOS simulation for a signal
 */
export const createSimulation = (signalId: string, metadata?: Record<string, any>) => {
  return fetchWithAuth('/v1/ombudsman-decisions/simulations', {
    method: 'POST',
    body: JSON.stringify({ signal_id: signalId, metadata })
  });
};

/**
 * Trigger backfill of ombudsman vector columns (admin action, org-scoped)
 */
export const backfillOmbudsmanVectors = () => {
  return fetchWithAuth('/v1/ombudsman-decisions/backfill-vectors', {
    method: 'POST'
  });
};

/**
 * Evidence API Functions
 */

/**
 * Upload evidence file for a signal
 */
export const uploadEvidenceForSignal = (signalId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return fetchWithAuth(`/v1/ombudsman-decisions/evidence?signal_id=${signalId}`, {
    method: 'POST',
    body: formData
  });
};

/**
 * Upload evidence file for an existing signal (alternative endpoint)
 */
export const uploadEvidenceForSignalV2 = (signalId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return fetchWithAuth(`/v1/signals/${signalId}/evidence`, {
    method: 'POST',
    body: formData
  });
};

/**
 * Get evidence files for a signal
 */
export const getEvidenceForSignal = (signalId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/signals/${signalId}/evidence`);
};

/**
 * Get evidence files for a simulation (with used_in_reasoning flags)
 */
export const getEvidenceForSimulation = (simulationId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/simulations/${simulationId}/evidence`);
};

/**
 * Toggle used_in_reasoning flag for evidence in a simulation
 */
export const toggleEvidenceInSimulation = (
  simulationId: string,
  evidenceId: string,
  usedInReasoning: boolean
) => {
  return fetchWithAuth(
    `/v1/ombudsman-decisions/simulations/${simulationId}/evidence/${evidenceId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ used_in_reasoning: usedInReasoning })
    }
  );
};

/**
 * Delete an evidence file
 */
export const deleteEvidence = (evidenceId: string) => {
  return fetchWithAuth(`/v1/ombudsman-decisions/evidence/${evidenceId}`, {
    method: 'DELETE'
  });
};

export default {
  fetchWithAuth,
  getApi,
  getCurrentUser,
  getComplaints,
  getPaginatedComplaints,
  getComplaintById,
  submitComplaint,
  reclassifyComplaint,
  deleteComplaint,
  deleteMultipleComplaints,
  getClusters,
  getPaginatedClusters,
  getClusterById,
  getExploreClusters,
  getExploreClustersSummary,
  getExploreClustersTimeline,
  generateClusters,
  generateClustersBackground,
  getClusterJobStatus,
  getAllClusterJobs,
  getClusteringDiagnostics,
  getAllClustersDebug,
  createPollingFunction,
  getPaginatedSignals,
  getSignalById,
  ingestSignal,
  createSignal,
  updateSignal,
  deleteSignal,
  reclassifySignal,
  bulkReclassifySignals,
  processFileForSignal,
  processFileAndCreateDraft,
  finalizeDraftSignal,
  processFileForMultipleSignals,
  isAdminUser,
  getOmbudsmanDecisions,
  getOmbudsmanDecisionById,
  importOmbudsmanDecisions,
  deleteOmbudsmanDecisions,
  getOmbudsmanStatistics,
  getSignalOmbudsmanAdvice,
  getDecisionRelatedSignals,
  backfillOmbudsmanVectors,
  uploadEvidenceForSignal,
  uploadEvidenceForSignalV2,
  getEvidenceForSignal,
  getEvidenceForSimulation,
  toggleEvidenceInSimulation,
  deleteEvidence,
  getOrganizationDisplaySettings,
  updateOrganizationDisplaySettings,
  // FOS Explorer
  searchFOSDecisions,
  searchFOSDecisionsGet,
  getFOSFacets,
  getFOSStats,
  getFOSDecisionById,
  getSimilarFOSDecisions,
  getFOSProjections,
  getFOSSignalProjections,
  refreshFOSSignalProjections
};
